import { RowType as RowTypes } from "./rows"
import { Chart, useChart } from "@chakra-ui/charts"
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts"
import { OnDataCallback } from "./Reporting"
import { Box, Skeleton, VStack } from "@chakra-ui/react"
import { Heading } from "@chakra-ui/react"
import Informative from "@hydrologiq/shared/Informative"
interface ReportingChartImplProps<RowType extends RowTypes> {
  rows: RowType[]
  loading?: boolean
  field: keyof RowType
  label: string
  color: string
  unit: string
}

export function ReportingChartSkeleton({
  title = true,
  chart,
  titleText,
}: {
  title?: boolean
  chart?: React.ReactNode
  titleText?: string
}) {
  return (
    <VStack pt={2} pb={10} gap={5} alignItems={"flex-start"} width="100%">
      {title && (
        <Heading as="h3" size={"md"}>
          {titleText || "Hydrogen consumption (first 10 entries)"}
        </Heading>
      )}
      {chart ? chart : <Skeleton width="100%" height="500px" />}
    </VStack>
  )
}

function ReportingChartImpl<RowType extends RowTypes>({
  rows,
  field,
  label,
  color,
  unit,
}: ReportingChartImplProps<RowType>) {
  // Explicitly handle value as number, ensure field is a string for object keys
  const data = rows.map((row) => ({
    [field as string]: Math.round(Number(row[field] ?? 0) * 10) / 10,
    start: (row as any).start,
  }))

  const maxValue = Math.ceil(Math.max(...data.map((row) => Number(row[field as string] ?? 0))))

  const chart = useChart({
    data,
    series: [{ name: field as string, color, label: label }],
  })
  const dateFormatter = chart.formatDate({
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return rows.length === 0 ? (
    <Informative heading="No data available" text="No data available for the selected period." />
  ) : (
    <Chart.Root height="500px" chart={chart}>
      <BarChart data={chart.data}>
        <CartesianGrid stroke={chart.color("border.muted")} vertical={false} />
        <XAxis axisLine={false} tickLine={false} dataKey={chart.key("start")} tickFormatter={dateFormatter} />
        <Tooltip
          cursor={{ fill: chart.color("bg.muted") }}
          labelFormatter={dateFormatter}
          animationDuration={0}
          content={<Chart.Tooltip />}
        />
        <YAxis axisLine={false} tickLine={false} domain={[0, maxValue]} tickFormatter={(value) => `${value} ${unit}`} />
        {chart.series.map((item) => (
          <Bar
            key={item.name}
            isAnimationActive={false}
            dataKey={chart.key(item.name)}
            fill={chart.color(item.color)}
          />
        ))}
      </BarChart>
    </Chart.Root>
  )
}

interface ReportingChartProps<RowType extends RowTypes> {
  onDataCallback: OnDataCallback<RowType>
  loading?: boolean
  title?: boolean
  fields?: string[]
}

interface ReportingFieldConfig {
  field: keyof RowTypes
  label: string
  color: string
  unit: string
  title: string
}

const FIELD_CONFIG: Record<string, ReportingFieldConfig> = {
  usedWeight: {
    field: "usedWeight",
    label: "Weight used (kg)",
    color: "#39aed2",
    unit: "kg",
    title: "Hydrogen consumption (first 10 entries)",
  },
  usedEnergy: {
    field: "usedEnergy",
    label: "Energy used (kWh)",
    color: "#39aed2",
    unit: "kWh",
    title: "Energy used (first 10 entries)",
  },
}

export function ReportingChart<RowType extends RowTypes>({
  onDataCallback,
  loading,
  title = true,
  fields = ["usedWeight"],
}: ReportingChartProps<RowType>) {
  const charts = fields
    .filter((field) => field in FIELD_CONFIG)
    .map((field) => FIELD_CONFIG[field as string])
    .filter((config) => config !== undefined)

  return (
    <VStack align="stretch" gap={2}>
      {charts.map((chart) => (
        <ReportingChartSkeleton
          title={title}
          titleText={chart.title}
          chart={
            !loading && (
              <ReportingChartImpl
                rows={onDataCallback()}
                loading={loading}
                field={chart.field}
                label={chart.label}
                color={chart.color}
                unit={chart.unit}
              />
            )
          }
        />
      ))}
    </VStack>
  )
}

export default ReportingChart
